"use client";

import { useEffect, useRef, type ComponentPropsWithoutRef, type KeyboardEvent, type ReactNode } from "react";

import { cx } from "../../lib/cx";
import s from "./Drawer.module.css";

export interface DrawerProps extends Omit<ComponentPropsWithoutRef<"aside">, "title"> {
  /** Cerrado no renderiza nada. */
  open?: boolean;
  /** Nombre accesible del panel ("Detalle de visita"). */
  label: string;
  /** Escape y click en el fondo. Sin `onClose` el panel no se cierra desde afuera. */
  onClose?: () => void;
  /** Arriba: típicamente un `Badge` de estado y el botón de cerrar. Va dentro del área que se desplaza. */
  header?: ReactNode;
  children?: ReactNode;
  /** La fila de acciones, pegada abajo con su regla. */
  footer?: ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Panel lateral que entra desde la derecha sobre un scrim: el detalle de una
 * fila sin salir de la lista. No es un `<dialog>` nativo a propósito: un
 * `Dialog` abierto desde adentro (confirmar, reprogramar) tiene que quedar por
 * encima, y el top layer del browser apila por orden de apertura.
 */
export function Drawer({ open = true, label, onClose, header, children, footer, className, onKeyDown, ...rest }: DrawerProps) {
  const panel = useRef<HTMLElement>(null);

  // Al abrir: foco adentro y el fondo sin desplazamiento; al cerrar, el foco
  // vuelve a donde estaba (la fila que lo abrió).
  useEffect(() => {
    if (!open) return;
    const previo = document.activeElement as HTMLElement | null;
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const primero = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (primero ?? panel.current)?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflowPrevio;
      previo?.focus?.({ preventScroll: true });
    };
  }, [open]);

  // Escape cierra aunque el foco haya quedado afuera (después de una acción que
  // volvió a pedir la página, por ejemplo). Con un `<dialog>` abierto encima,
  // Escape es del diálogo: el browser le manda `cancel` y el panel no se mete.
  useEffect(() => {
    if (!open || !onClose) return;
    const alTeclear = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector("dialog[open]")) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [open, onClose]);

  if (!open) return null;

  // Tab queda adentro del panel mientras esté abierto.
  const alTeclear = (e: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || e.key !== "Tab" || !panel.current) return;
    const focusables = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const primero = focusables[0]!;
    const ultimo = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  };

  return (
    <div className={s.capa}>
      <div className={s.scrim} onClick={onClose} aria-hidden="true" />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cx(s.panel, className)}
        onKeyDown={alTeclear}
        {...rest}
      >
        <div className={s.cuerpo}>
          {header ? <div className={s.cabecera}>{header}</div> : null}
          {children}
        </div>
        {footer ? <div className={s.pie}>{footer}</div> : null}
      </aside>
    </div>
  );
}
